import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Badge, Button, DataTable, IconButton, Input, Modal, Select, Spin, useAlert } from '../ui';
import { DataTableColumn } from '../ui/DataTable';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import { Ban, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import FormDrawer from '../ui/FormDrawer';
import InfoBar from '../ui/InfoBar';
import PageHeader from '../ui/PageHeader';
import TableStatusBar from '../ui/TableStatusBar';
import FormCreneau, { CreneauData } from './FormCreneau';

type StatutCreneau = 'LIBRE' | 'RESERVE' | 'ANNULE' | 'HONORE';

/** Normalise le statut d'un créneau quel que soit le nom du champ renvoyé par l'API. */
function statutCreneau(row: any): StatutCreneau {
    const raw = row?.Statut ?? row?.StatutCreneau ?? row?.Etat ?? null;
    if (raw != null && raw !== '') {
        const s = String(raw).toUpperCase();
        if (s.includes('ANNUL')) return 'ANNULE';
        if (s.includes('HONOR')) return 'HONORE';
        if (s.includes('RESERV') || s.includes('OCCUP') || s === '1') return 'RESERVE';
        if (s.includes('LIBRE') || s === '0') return 'LIBRE';
    }
    if (row?.EstLibre === true || row?.EstLibre === 1) return 'LIBRE';
    if (row?.EstLibre === false || row?.EstLibre === 0) return 'RESERVE';
    if (row?.CodeClient) return 'RESERVE';
    return 'LIBRE';
}

const STATUT_BADGE: Record<StatutCreneau, { color: 'green' | 'blue' | 'gray' | 'violet'; label: string }> = {
    LIBRE: { color: 'green', label: 'Libre' },
    RESERVE: { color: 'blue', label: 'Réservé' },
    ANNULE: { color: 'gray', label: 'Annulé' },
    HONORE: { color: 'violet', label: 'Honoré' },
};

/** Formate une date API en libellé lisible (fr). */
function formatDateHeure(value: any): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function GestionCreneaux() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [data, setData] = useState<any[]>([])
    const [loading, setLoading] = useState(false)

    // Filtres
    const [filiales, setFiliales] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])
    const [filtreFiliale, setFiltreFiliale] = useState('')
    const [filtreService, setFiltreService] = useState('')
    const [filtreDate, setFiltreDate] = useState('')

    // Drawer création / édition
    const [open, setOpen] = useState(false)
    const [creneau, setCreneau] = useState<CreneauData | null>(null)

    // Modale de confirmation (DELETE / ANNULER)
    const [confirm, setConfirm] = useState<{ open: boolean; action: 'DELETE' | 'ANNULER'; row: any | null }>({ open: false, action: 'DELETE', row: null })
    const [actionLoading, setActionLoading] = useState(false)

    // Ligne sélectionnée (InfoBar)
    const [selected, setSelected] = useState<any>()

    useEffect(() => {
        LoadData()
        LoadFiliales()
        LoadServices()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Liste les créneaux (Option LISTER) selon les filtres filiale / service. */
    function LoadData() {
        setLoading(true)
        const payload = {
            Option: 'LISTER',
            CodeAbonne: user.CodeAbonne,
            CodeFiliale: filtreFiliale || null,
            CodeS: filtreService || null,
        }
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Creneaux.ManageCreneau,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response: any) => {
                setLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    const list = Array.isArray(res.Result)
                        ? res.Result
                        : (res.Result?.Table || res.Result?.Table1 || [])
                    // Tri par date décroissante + statut normalisé pour l'affichage
                    const rows = list
                        .map((c: any, i: number) => ({ id: c.Id ?? i, ...c, _statut: statutCreneau(c) }))
                        .sort((a: any, b: any) => String(b.DateHeure || '').localeCompare(String(a.DateHeure || '')))
                    setData(rows)
                }
                else {
                    setData([])
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    function LoadFiliales() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    setFiliales(Array.isArray(res.Result) ? res.Result : [])
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    function LoadServices() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetFilialeServices + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    // GetFilialeServices renvoie [filiales, services] — tolérer un tableau simple
                    if (Array.isArray(res.Result) && Array.isArray(res.Result[0])) {
                        setServices(res.Result[1] || [])
                    } else {
                        setServices(Array.isArray(res.Result) ? res.Result : [])
                    }
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    /** Appel générique ManageCreneau (DELETE, ANNULER...). */
    function ManageCreneau(payload: any, onSuccess?: () => void) {
        setActionLoading(true)
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Creneaux.ManageCreneau,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response: any) => {
                setActionLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    onSuccess?.()
                    LoadData()
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setActionLoading(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    function handleConfirm() {
        const row = confirm.row
        if (!row) return
        if (confirm.action === 'DELETE') {
            ManageCreneau(
                { Option: 'DELETE', Id: row.Id, CodeAbonne: user.CodeAbonne },
                () => setConfirm({ open: false, action: 'DELETE', row: null })
            )
        } else {
            ManageCreneau(
                { Option: 'ANNULER', Id: row.Id, CodeClient: row.CodeClient ?? null, CodeAbonne: user.CodeAbonne },
                () => setConfirm({ open: false, action: 'ANNULER', row: null })
            )
        }
    }

    function openEdit(row: any) {
        setCreneau(row)
        setOpen(true)
    }

    function openCreate() {
        setCreneau(null)
        setOpen(true)
    }

    // Filtrage local par jour (le payload LISTER n'a pas de paramètre date)
    const rowsFiltres = filtreDate
        ? data.filter((r: any) => String(r.DateHeure || '').slice(0, 10) === filtreDate)
        : data

    const servicesFiltres = services.filter((s: any) =>
        !filtreFiliale || s.CodeFiliale == null || s.CodeFiliale === filtreFiliale
    );

    const columns: DataTableColumn[] =
        [
            { field: 'Id', headerName: 'ID', width: 70 },
            {
                field: 'DateHeure', headerName: 'Date / heure', width: 160,
                renderCell: ({ value }) => <span>{formatDateHeure(value)}</span>,
            },
            {
                field: 'DureeMinutes', headerName: 'Durée', width: 90,
                renderCell: ({ value }) => <span>{value != null ? `${value} min` : '—'}</span>,
            },
            { field: 'CodeFiliale', headerName: 'Filiale', width: 110 },
            { field: 'CodeS', headerName: 'Service', width: 100 },
            {
                field: 'CodeClient', headerName: 'Client', width: 120,
                renderCell: ({ value }) => <span>{value || '—'}</span>,
            },
            {
                field: '_statut', headerName: 'Statut', width: 100,
                renderCell: ({ value }) => {
                    const s = STATUT_BADGE[value as StatutCreneau] || STATUT_BADGE.LIBRE
                    return <Badge color={s.color}>{s.label}</Badge>
                },
            },
            {
                field: 'Actions', headerName: 'Actions', width: 140, sortable: false,
                renderCell: ({ row }) => {
                    const statut: StatutCreneau = row._statut
                    const isLibre = statut === 'LIBRE'
                    const isReserve = statut === 'RESERVE'
                    return (
                        <div className="flex items-center gap-1">
                            <IconButton
                                tooltip={isLibre ? 'Modifier le créneau' : 'Modification impossible : créneau réservé'}
                                color='primary'
                                size={30}
                                disabled={!isLibre}
                                onClick={(e) => { e.stopPropagation(); openEdit(row) }}
                            >
                                <Pencil size={15} />
                            </IconButton>
                            {isReserve && (
                                <IconButton
                                    tooltip='Annuler le rendez-vous'
                                    color='warning'
                                    size={30}
                                    onClick={(e) => { e.stopPropagation(); setConfirm({ open: true, action: 'ANNULER', row }) }}
                                >
                                    <Ban size={15} />
                                </IconButton>
                            )}
                            <IconButton
                                tooltip={isLibre ? 'Supprimer le créneau' : 'Suppression impossible : créneau réservé'}
                                color='danger'
                                size={30}
                                disabled={!isLibre}
                                onClick={(e) => { e.stopPropagation(); setConfirm({ open: true, action: 'DELETE', row }) }}
                            >
                                <Trash2 size={15} />
                            </IconButton>
                        </div>
                    )
                },
            },
        ];

    return (
        <Spin spinning={loading} >
            <PageHeader
                title="Créneaux de rendez-vous"
                subtitle="Ouvrez et gérez l'agenda des créneaux par agence et service"
                actions={
                    <Button
                        size='sm'
                        variant="primary"
                        startIcon={<Plus size={16} />}
                        onClick={openCreate}
                    >Nouveau créneau</Button>
                }
            />

            {/* Filtres */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 items-end">
                <Select
                    label="Filiale"
                    value={filtreFiliale}
                    onChange={setFiltreFiliale}
                    options={[{ value: '', label: 'Toutes les filiales' }, ...filiales.map((f: any) => ({ value: f.CodeFiliale, label: f.CodeFiliale + ' - ' + f.Libelle }))]}
                />
                <Select
                    label="Service"
                    value={filtreService}
                    onChange={setFiltreService}
                    options={[{ value: '', label: 'Tous les services' }, ...servicesFiltres.map((s: any) => ({ value: s.CodeS, label: s.CodeS + ' - ' + s.Libelle }))]}
                />
                <Input
                    type='date'
                    label='Jour'
                    value={filtreDate}
                    onChange={(e: any) => setFiltreDate(e.target.value)}
                />
                <div>
                    <Button
                        variant='secondary'
                        size='md'
                        startIcon={<RefreshCw size={15} />}
                        onClick={LoadData}
                        loading={loading}
                    >Actualiser</Button>
                </div>
            </div>

            <div className="eb-table-wrap">
                <DataTable
                    onRowClick={(row: any) => setSelected(row)}
                    rows={rowsFiltres}
                    height={380}
                    columns={columns}
                    searchable
                    emptyText="Aucun créneau"
                />
            </div>
            <TableStatusBar count={rowsFiltres.length} loading={loading} onRefresh={LoadData} />
            <InfoBar data={[
                { label: 'Créneau', value: selected?.Id != null ? `#${selected.Id} — ${formatDateHeure(selected?.DateHeure)}` : undefined },
                { label: 'Créé par', value: selected?.CreatedBy },
                { label: 'Date Modif', value: selected?.ModifDate },
            ]} />

            {/* Drawer création / modification */}
            <FormDrawer
                open={open}
                onClose={() => setOpen(false)}
                title={creneau ? `Modification du créneau #${creneau.Id}` : 'Nouveau créneau'}
                hint={creneau
                    ? "Les informations existantes sont pré-remplies. La modification n'est possible que tant que le créneau est libre."
                    : "Renseignez la filiale, le service, la date/heure et la durée du créneau à ouvrir."}
            >
                <FormCreneau
                    creneau={creneau}
                    filiales={filiales}
                    services={services}
                    onSaved={() => { setOpen(false); LoadData() }}
                />
            </FormDrawer>

            {/* Confirmation suppression / annulation */}
            <Modal
                open={confirm.open}
                onClose={() => setConfirm({ ...confirm, open: false })}
                title={confirm.action === 'DELETE' ? 'Supprimer le créneau' : 'Annuler le rendez-vous'}
                footer={
                    <>
                        <Button variant='outline' onClick={() => setConfirm({ ...confirm, open: false })}>Fermer</Button>
                        <Button
                            variant={confirm.action === 'DELETE' ? 'danger' : 'warning'}
                            loading={actionLoading}
                            onClick={handleConfirm}
                        >Confirmer</Button>
                    </>
                }
            >
                {confirm.action === 'DELETE' ? (
                    <p className="text-sm text-[var(--eb-text)]">
                        Voulez-vous vraiment supprimer le créneau <strong>#{confirm.row?.Id}</strong> du <strong>{formatDateHeure(confirm.row?.DateHeure)}</strong> ? Cette action est définitive.
                    </p>
                ) : (
                    <p className="text-sm text-[var(--eb-text)]">
                        Voulez-vous annuler le rendez-vous du client <strong>{confirm.row?.CodeClient}</strong> sur le créneau du <strong>{formatDateHeure(confirm.row?.DateHeure)}</strong> ?
                    </p>
                )}
            </Modal>
        </Spin>
    );
}
